
import { Link } from "react-router-dom";
import { Instagram, Facebook, Twitter } from "lucide-react";
import { useState } from "react";

const footerLinks = {
  shop: [
    { label: "New Arrivals", href: "/categories/new" },
    { label: "Best Sellers", href: "/categories/best-sellers" },
    { label: "Sale", href: "/categories/sale" },
    { label: "All Products", href: "/products" },
  ],
  help: [
    { label: "FAQ", href: "/help/faq" },
    { label: "Shipping", href: "/help/shipping" },
    { label: "Returns", href: "/help/returns" },
    { label: "Contact", href: "/contact" },
  ],
  company: [
    { label: "About Us", href: "/about" },
    { label: "Careers", href: "/careers" },
    { label: "Privacy Policy", href: "/privacy" },
    { label: "Terms of Service", href: "/terms" },
  ],
};

const socialLinks = [
  { icon: Instagram, href: "https://instagram.com", label: "Instagram" },
  { icon: Facebook, href: "https://facebook.com", label: "Facebook" },
  { icon: Twitter, href: "https://twitter.com", label: "Twitter" },
];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail("");
    }
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{
        __html: `
          footer[data-theme-footer] * {
            color: hsl(var(--footer-foreground)) !important;
          }
          footer[data-theme-footer] a:hover {
            opacity: 0.8;
          }
        `
      }} />
      <footer
        data-theme-footer
        style={{
          backgroundColor: `hsl(var(--footer-background))`,
          color: `hsl(var(--footer-foreground))`
        }}
        className="border-t"
      >
      {/* Newsletter Section */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="py-12 lg:py-16">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div>
              <h3 className="text-lg font-medium tracking-tight">
                Subscribe to our newsletter
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Be the first to know about new collections and exclusive offers.
              </p>
            </div>
            <form onSubmit={handleSubscribe} className="flex gap-3 w-full lg:w-auto">
              {subscribed ? (
                <p className="text-sm text-muted-foreground py-3">
                  Thank you for subscribing!
                </p>
              ) : (
                <>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="flex-1 lg:w-72 h-11 px-4 rounded-none border border-border bg-background text-sm placeholder:text-muted-foreground focus:outline-none focus:border-foreground transition-colors"
                    required
                  />
                  <button
                    type="submit"
                    className="h-11 px-6 bg-foreground text-background text-sm font-medium hover:bg-foreground/90 transition-colors"
                  >
                    Subscribe
                  </button>
                </>
              )}
            </form>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t" />

        {/* Links Section */}
        <div className="py-12 lg:py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {/* Shop Links */}
            <div>
              <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-4">
                Shop
              </h4>
              <ul className="space-y-3">
                {footerLinks.shop.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-sm text-foreground/70 hover:text-foreground transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Help Links */}
            <div>
              <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-4">
                Help
              </h4>
              <ul className="space-y-3">
                {footerLinks.help.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-sm text-foreground/70 hover:text-foreground transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company Links */}
            <div>
              <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-4">
                Company
              </h4>
              <ul className="space-y-3">
                {footerLinks.company.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-sm text-foreground/70 hover:text-foreground transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Social Links */}
            <div>
              <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-4">
                Follow Us
              </h4>
              <div className="flex gap-4">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-foreground/70 hover:text-foreground transition-colors"
                    aria-label={social.label}
                  >
                    <social.icon className="h-5 w-5" strokeWidth={1.5} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t py-6">
          <p className="text-xs text-muted-foreground text-center">
            &copy; {new Date().getFullYear()} Store. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
    </>
  );
};
